import React, { useEffect, useMemo, useRef, useState } from "react";

import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

import { getDataCategoryGroup } from "pages/dataCollection/_shared/categoryGroups";
import { DATA_COLLECTION_CONFIGS } from "pages/dataCollection/_shared/config";
import { uploadToS3 as uploadFileWithPresigned } from "shared/api/awsS3";
import { useDirtyCheck } from "shared/lib/form/useDirtyCheck";
import CircularProgressIndicator from "shared/ui/CircularProgressIndicator";
import { alertDialog, confirmDialog, confirmLeaveIfDirty } from "shared/ui/feedback";
import { ChipGroup } from "shared/ui/filters";
import { FormPageShell } from "shared/ui/layout";
import { FileDropzone } from "shared/ui/upload";

const XLSX_CONTENT_TYPE =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

const CKEDITOR_CONFIG = {
  placeholder: "내용을 입력하세요.",
  enterMode: "ClassicEditor.ENTER_BR",
  shiftEnterMode: "ClassicEditor.ENTER_BR",
  toolbar: {
    items: [
      "heading",
      "|",
      "bold",
      "italic",
      "link",
      "bulletedList",
      "numberedList",
      "blockQuote",
    ],
  },
};

const COPY = {
  create: {
    verb: "등록",
    submit: "등록하기",
    confirm: "해당 자료를 등록하시겠습니까?\n자료집 리스트에서 내용 수정 가능합니다.",
    leave: "자료 등록을 취소하시겠습니까?\n취소시 작성 중인 내용이 사라집니다.",
  },
  edit: {
    verb: "수정",
    submit: "수정하기",
    confirm: "해당 자료를 수정하시겠습니까?",
    leave: "자료 수정을 취소하시겠습니까?\n취소시 수정 중인 내용이 취소됩니다.",
  },
};

// 첨부파일 목록을 비교 가능한 문자열로 (추가/삭제 시 값이 달라진다)
// 서버가 내려준 기존 첨부는 id·name이 없고 {title, link, size}만 있으므로,
// 존재하는 식별자를 순서대로 사용해 파일마다 고유한 값이 나오게 한다.
// (link 미보유 신규 파일은 name/size로 구분)
const fileSignature = (list) =>
  (list || [])
    .map(
      (file) =>
        `${
          file?.id ??
          file?.localId ??
          file?.link ??
          file?.name ??
          file?.title ??
          ""
        }:${file?.size ?? ""}`,
    )
    .join("|");

/**
 * 자료실 등록·수정 공용 폼.
 *
 * 생성/수정의 차이는 (1)초기값 (2)첨부 업로드 정책 (3)제출 페이로드 (4)문구뿐이라
 * props로 주입받아 한 컴포넌트로 처리한다. 데이터 로드/뮤테이션은 래퍼가 담당한다.
 *
 * @param {"create"|"edit"} mode
 * @param {number} groupId          - 카테고리 그룹 기준 id(칩 옵션·헤더 라벨용)
 * @param {object|null} initialValues - {title, category, pin, content, files}. edit 로드 전 null.
 * @param {boolean} isLoading
 * @param {string}  loadError
 * @param {(data:object)=>Promise<void>} onSubmit - 생성/수정 API 호출
 */
export function DataForm({
  mode,
  groupId,
  initialValues = null,
  isLoading = false,
  loadError = "",
  onSubmit,
}) {
  const copy = COPY[mode];
  const navigate = useNavigate();

  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [files, setFiles] = useState([]);
  const [pin, setPin] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [delFile, setDelFile] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const { isDirty, captureBaseline } = useDirtyCheck({
    title,
    category: selectedCategory,
    pin,
    content,
    files: fileSignature(files),
  });

  // 초기값 1회 주입 + 변경 비교 기준값 기록 (create: 빈 값 / edit: 로드된 값).
  const initializedRef = useRef(false);
  useEffect(() => {
    if (initializedRef.current || !initialValues) return;
    initializedRef.current = true;
    setTitle(initialValues.title || "");
    setContent(initialValues.content || "");
    setFiles(initialValues.files || []);
    setPin(Boolean(initialValues.pin));
    setSelectedCategory(initialValues.category ?? null);
    captureBaseline({
      title: initialValues.title || "",
      category: initialValues.category ?? null,
      pin: Boolean(initialValues.pin),
      content: initialValues.content || "",
      files: fileSignature(initialValues.files),
    });
  }, [initialValues, captureBaseline]);

  // 칩 옵션은 그룹 기준 id로 고정(선택 해제로 selectedCategory가 null이 돼도 유지).
  const categoryOptions = useMemo(() => {
    const matchedConfig = Object.values(DATA_COLLECTION_CONFIGS).find((cfg) =>
      cfg.categories.some((item) => item.id === Number(groupId)),
    );
    return matchedConfig ? matchedConfig.categories : [];
  }, [groupId]);

  const groupLabel = getDataCategoryGroup(groupId).label;
  const headerTitle = groupLabel
    ? `${groupLabel} - 자료 ${copy.verb}하기`
    : "";

  const isAllFieldsFilled = Boolean(title && selectedCategory && content);
  const canSubmit = isAllFieldsFilled && isDirty && !submitting;

  // 드롭존 변경: 삭제된 "기존 첨부(id 보유)"는 서버 삭제 요청용으로 모은다(create는 항상 빈 값).
  const handleFilesChange = (next) => {
    const removedExistingIds = files
      .filter((file) => file?.id != null && !next.includes(file))
      .map((file) => file.id);
    if (removedExistingIds.length > 0) {
      setDelFile((prev) => [...prev, ...removedExistingIds]);
    }
    setFiles(next);
  };

  // 업로드 정책: create는 모든 파일, edit는 새로 추가한 File/Blob만(기존은 그대로 유지).
  const uploadFiles = async () => {
    try {
      // 서버가 fileKeys 필드에 S3 key 배열을 기대한다(새로 업로드한 파일만 전송).
      const fileKeys = [];
      if (mode === "create") {
        for (const file of files) {
          file.loading = true;
          const result = await uploadFileWithPresigned(file, false);
          const key = result?.key || "";
          if (!key) throw new Error("업로드 key를 찾을 수 없습니다.");
          fileKeys.push(key);
          file.loading = false;
        }
      } else {
        for (const file of files) {
          if (!(file instanceof File || file instanceof Blob)) continue;
          const newFileName = decodeURIComponent(file.name);
          const isXLSX = file.name.endsWith(".xlsx");
          const contentType = isXLSX ? XLSX_CONTENT_TYPE : file.type;
          const truncatedFileName = newFileName.substring(0, 20);
          const fileExtension = isXLSX
            ? ".xlsx"
            : newFileName.substring(newFileName.lastIndexOf("."));
          const finalFileName = truncatedFileName + fileExtension;
          const result = await uploadFileWithPresigned(
            new File([file], finalFileName, { type: contentType }),
          );
          fileKeys.push(result.key);
        }
      }
      return fileKeys;
    } catch (error) {
      alertDialog("파일 전송에 실패했습니다.");
      throw error;
    }
  };

  const buildPayload = (fileKeys) => {
    if (mode === "create") {
      return {
        titles: fileKeys.map((_, index) => `${title} 첨부파일 (${index + 1})`),
        fileKeys,
        category: parseInt(selectedCategory, 10),
        pin,
        title,
        content,
      };
    }
    // 남아있는 기존 첨부 개수만큼 번호를 이어서 매긴다.
    const remainingExistingCount = files.filter(
      (file) => file && file.id && file.link,
    ).length;
    return {
      titles: fileKeys.map(
        (_, index) => `${title} 첨부파일 (${remainingExistingCount + index + 1})`,
      ),
      fileKeys,
      delFiles: delFile,
      category: selectedCategory,
      title,
      content,
      hide: true,
      pin,
    };
  };

  const handleBack = async () => {
    const shouldLeave = await confirmLeaveIfDirty(isDirty, copy.leave);
    if (shouldLeave) {
      navigate(getDataCategoryGroup(selectedCategory ?? groupId).path);
    }
  };

  const handleSubmit = async () => {
    if (submitting) return;
    const shouldProceed = await confirmDialog(copy.confirm);
    if (!shouldProceed) return;

    if (title === "") {
      alertDialog("제목을 입력해주세요");
      return;
    }
    if (title.length > 255) {
      alertDialog("제목은 255자 제한입니다");
      return;
    }
    if (content === "") {
      alertDialog("내용을 입력해주세요");
      return;
    }
    if (content.length > 5000) {
      alertDialog("본문은 5000자 제한입니다");
      return;
    }

    try {
      setSubmitting(true);
      const fileKeys = await uploadFiles();
      await onSubmit(buildPayload(fileKeys));
      navigate(getDataCategoryGroup(selectedCategory).path);
    } catch (error) {
      // uploadFiles에서 실패 안내를 이미 표시. 페이지에 머문다.
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <FormPageShell
      title={headerTitle}
      onBack={handleBack}
      isLoading={isLoading}
      loadError={loadError}
      submitLabel={copy.submit}
      canSubmit={canSubmit}
      onSubmit={handleSubmit}
    >
      <CircularProgressIndicator loading={submitting} msg="저장 중.." />
      <RegisterDiv>
        <RowDiv>
          <Title>
            제목<RedAsterisk>*</RedAsterisk>
          </Title>
          <TextInput
            type="text"
            placeholder="제목입력"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </RowDiv>
        <RowDiv>
          <Writer>
            카테고리<RedAsterisk>*</RedAsterisk>
          </Writer>
          <ChipGroup
            options={categoryOptions.map((item) => ({
              label: item.label,
              value: item.id,
            }))}
            value={selectedCategory}
            onChange={setSelectedCategory}
            allowDeselect={mode === "edit"}
          />
        </RowDiv>
        <FileDiv>
          <Title>첨부파일</Title>
          <FilePutDiv>
            <FileDropzone files={files} onChange={handleFilesChange} />
          </FilePutDiv>
        </FileDiv>
        <PinDiv>
          <label
            style={{ flex: "0 0 80px", width: "80px", whiteSpace: "nowrap" }}
            htmlFor="pin"
          >
            고정여부
          </label>
          <PinDiv2>
            <input
              type="checkbox"
              id="pin"
              checked={pin}
              onChange={() => setPin(!pin)}
            />
            고정
          </PinDiv2>
        </PinDiv>
      </RegisterDiv>
      <TextEditor>
        <CKEditor
          editor={ClassicEditor}
          data={content}
          onChange={(event, editor) => setContent(editor.getData())}
          config={CKEDITOR_CONFIG}
        />
      </TextEditor>
    </FormPageShell>
  );
}

export default DataForm;

const RegisterDiv = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  width: 100%;
  height: fit-content;
  margin-top: 10px;
  padding: 20px;
  gap: 13px;
  background-color: white;
  border-radius: 4px;
`;

const RowDiv = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  gap: 30px;
`;

const FilePutDiv = styled.div`
  display: flex;
  width: 100%;
  flex-direction: row;
  align-items: center;
`;

const Title = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  flex: 0 0 80px;
  width: 80px;
  white-space: nowrap;
`;

const Writer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  flex: 0 0 80px;
  width: 80px;
  white-space: nowrap;
`;

const TextInput = styled.input`
  width: 100%;
  height: 35px;
  border-radius: 2px;
  border: 1px solid #e8e8e8;
  padding: 2px 10px;
  &::placeholder {
    opacity: 0.2;
  }
`;

const TextEditor = styled.div`
  width: 100%;
  height: 350px;
  margin-top: 20px;
  overflow: auto;

  .ck-editor__editable {
    height: 310px !important;
    overflow-y: scroll;
    scrollbar-width: thin;
    scrollbar-color: #ccc transparent;
  }
`;

const FileDiv = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  width: 100%;
  gap: 30px;
`;

const RedAsterisk = styled.span`
  font-size: ${(props) => props.theme.Web_fontSizes.Body3};
  font-weight: ${(props) => props.theme.fontWeights.Body3};
  line-height: ${(props) => props.theme.LineHeight.Body3};
  letter-spacing: -0.56px;
  font-style: normal;
  font-family: "Noto Sans KR";
  text-align: center;
  color: red;
`;

const PinDiv = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  gap: 30px;
`;

const PinDiv2 = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: 5px;
`;
